/**
 * Applies metaobject color swatches to product cards on collection/search grids.
 * Splits handle lookups across two section requests (early/late) to stay under
 * Shopify's per-render metaobject-by-handle budget (~20).
 */
(function () {
  const LINK_SELECTOR = 'card-product-swatches .card-product-swatches__link';
  const SECTION_IDS = [
    'benlee-swatch-lookup-early',
    'benlee-swatch-lookup-mid',
    'benlee-swatch-lookup-late',
  ];
  let lookupPromise = null;

  function handleize(value, { digraphs = true } = {}) {
    let text = String(value || '').toLowerCase();
    if (digraphs) {
      // Match Liquid handleize German transliteration: ü→ue, ö→oe, ä→ae, ß→ss
      text = text
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss');
    } else {
      // Admin metaobject handles often strip diacritics: ü→u
      text = text
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }
    return text.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function lookupCss(map, title) {
    const lower = title.toLowerCase();
    const ascii = lower
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ü/g, 'u')
      .replace(/ß/g, 'ss');
    return (
      map.get(lower) ||
      map.get(ascii) ||
      map.get(handleize(title, { digraphs: true })) ||
      map.get(handleize(title, { digraphs: false }))
    );
  }

  function parseLookup(raw) {
    const map = new Map();
    String(raw || '')
      .split('@@@')
      .forEach((entry) => {
        if (!entry) return;
        const parts = entry.split('<<<CSS>>>');
        if (parts.length < 2) return;
        const keys = parts[0];
        const css = parts.slice(1).join('<<<CSS>>>').trim();
        if (!css) return;
        // Split on |#| so adjacent keys that share a delimiter are all kept
        keys.split('|#|').forEach((token) => {
          const key = token.trim();
          if (key) map.set(key, css);
        });
      });
    return map;
  }

  function applyLookup(map) {
    if (!map || map.size === 0) return;
    document.querySelectorAll(LINK_SELECTOR).forEach((link) => {
      const title = (link.getAttribute('title') || link.getAttribute('aria-label') || '').trim();
      if (!title) return;
      const css = lookupCss(map, title);
      if (!css) return;
      const swatch = link.querySelector('.swatch');
      if (!swatch) return;
      swatch.style.setProperty('--swatch--background', css);
    });
    document.querySelectorAll('card-product-swatches').forEach((el) => {
      el.setAttribute('data-swatches-ready', 'true');
    });
  }

  function sectionUrl(sectionId) {
    const params = new URLSearchParams(window.location.search);
    params.set('section_id', sectionId);
    return `${window.location.pathname}?${params.toString()}`;
  }

  function fetchSectionLookup(sectionId) {
    return fetch(sectionUrl(sectionId))
      .then((response) => {
        if (!response.ok) throw new Error('swatch lookup failed: ' + sectionId);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const el = doc.getElementById('benlee-swatch-lookup-data');
        let raw = el ? el.textContent : '';
        try {
          raw = JSON.parse(raw);
        } catch (e) {
          // already plain text
        }
        return parseLookup(raw);
      });
  }

  function fetchLookup() {
    if (lookupPromise) return lookupPromise;

    lookupPromise = Promise.all(SECTION_IDS.map(fetchSectionLookup))
      .then((maps) => {
        const merged = new Map();
        maps.forEach((map) => {
          map.forEach((css, key) => merged.set(key, css));
        });
        return merged;
      })
      .catch((error) => {
        lookupPromise = null;
        console.warn('[benlee-swatch-lookup]', error);
        return new Map();
      });

    return lookupPromise;
  }

  function refreshSwatches() {
    if (!document.querySelector(LINK_SELECTOR)) return;
    // Reset cache after facet/grid replacements so collection page context is re-read.
    if (document.querySelector('card-product-swatches:not([data-swatches-ready])')) {
      lookupPromise = null;
    }
    fetchLookup().then(applyLookup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshSwatches, { once: true });
  } else {
    refreshSwatches();
  }

  document.addEventListener('shopify:section:load', () => {
    lookupPromise = null;
    refreshSwatches();
  });

  const grid = document.getElementById('ProductGridContainer');
  if (grid && typeof MutationObserver === 'function') {
    const observer = new MutationObserver(() => {
      lookupPromise = null;
      refreshSwatches();
    });
    observer.observe(grid, { childList: true, subtree: false });
  }
})();
